<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Table from 'dashboard/components-next/table/Table.vue';
import TableEmpty from 'dashboard/components-next/table/TableEmpty.vue';
import {
  sourceColor,
  buildTrackingUrl,
  relativeOrDate,
} from './attributionUtils';

// Codes table of the CrmB mockup: código · campaña · origen · leads de 30
// días con barra · último uso · copiar / editar.
const props = defineProps({
  campaigns: { type: Array, required: true },
  landingUrl: { type: String, default: '' },
});

const emit = defineEmits(['edit']);

const { t, locale } = useI18n();

const maxLeads = computed(() =>
  Math.max(
    1,
    ...props.campaigns.map(campaign => Number(campaign.leads_30d) || 0)
  )
);

const rows = computed(() =>
  props.campaigns.map(campaign => {
    const leads = Number(campaign.leads_30d) || 0;
    const source = campaign.utms?.source || '';
    return {
      ...campaign,
      leads,
      source,
      sourceDot: sourceColor(source),
      medium: campaign.utms?.medium || '',
      campaignName: campaign.utms?.campaign || campaign.description || '—',
      content: campaign.utms?.content || campaign.description || '',
      barWidth: `${Math.round((leads / maxLeads.value) * 100)}%`,
      lastUse: relativeOrDate(campaign.last_used_at, locale.value),
    };
  })
);

const columns = computed(() => [
  { key: 'code', label: t('ATTRIBUTION.TABLE.CODE'), width: 'w-[86px]' },
  { key: 'campaign', label: t('ATTRIBUTION.TABLE.CAMPAIGN') },
  { key: 'source', label: t('ATTRIBUTION.TABLE.SOURCE'), width: 'w-[206px]' },
  { key: 'leads', label: t('ATTRIBUTION.TABLE.LEADS_30D'), width: 'w-[140px]' },
  {
    key: 'lastUse',
    label: t('ATTRIBUTION.TABLE.LAST_USE'),
    width: 'w-[100px]',
    cellClass: 'text-[11.5px] text-n-slate-10 truncate',
  },
  { key: 'actions', label: '', width: 'w-[86px]' },
]);

const copyLink = async campaign => {
  const url = buildTrackingUrl(props.landingUrl, campaign.utms, campaign.code);
  if (!url) {
    useAlert(t('ATTRIBUTION.BUILDER.COPY_ERROR'));
    return;
  }
  try {
    await copyTextToClipboard(url);
    useAlert(t('ATTRIBUTION.BUILDER.COPIED'));
  } catch {
    useAlert(t('ATTRIBUTION.BUILDER.COPY_ERROR'));
  }
};

const ACTION_CLASS =
  'flex items-center justify-center size-[25px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-blue-text transition-colors';
</script>

<template>
  <section class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2">
    <Table :columns="columns" :rows="rows" density="regular">
      <template #cell-code="{ row }">
        <span
          class="inline-flex items-center h-[21px] px-2 rounded-md bg-n-solid-blue text-n-blue-text text-[11.5px] font-semibold font-mono"
        >
          {{ row.code }}
        </span>
      </template>

      <template #cell-campaign="{ row }">
        <div class="flex flex-col gap-0.5 min-w-0">
          <span
            class="text-[12.5px] font-semibold truncate text-n-slate-12"
            :title="row.campaignName"
          >
            {{ row.campaignName }}
          </span>
          <span
            v-if="row.content"
            class="text-[11.5px] truncate text-n-slate-10"
            :title="row.content"
          >
            {{ row.content }}
          </span>
        </div>
      </template>

      <template #cell-source="{ row }">
        <div class="flex items-center gap-1.5 min-w-0">
          <span
            v-if="row.source"
            class="inline-flex items-center flex-shrink-0 gap-1.5 h-[21px] px-2 rounded-md bg-n-alpha-2 text-[11.5px] font-semibold text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full"
              :style="{ backgroundColor: row.sourceDot }"
            />
            {{ row.source }}
          </span>
          <span
            v-if="row.medium"
            class="text-[11.5px] truncate text-n-slate-10"
          >
            {{ row.medium }}
          </span>
          <TableEmpty v-if="!row.source && !row.medium" />
        </div>
      </template>

      <template #cell-leads="{ row }">
        <div class="flex items-center gap-2.5">
          <div class="flex-1 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
            <div
              class="h-full rounded-full bg-n-brand"
              :style="{ width: row.barWidth }"
            />
          </div>
          <span
            class="text-[12.5px] font-semibold tabular-nums text-n-slate-12"
          >
            {{ row.leads }}
          </span>
        </div>
      </template>

      <template #cell-lastUse="{ row }">
        {{ row.lastUse || '—' }}
      </template>

      <template #cell-actions="{ row }">
        <div class="flex items-center justify-end gap-1.5">
          <button
            type="button"
            :class="ACTION_CLASS"
            :title="t('ATTRIBUTION.TABLE.COPY_LINK')"
            @click="copyLink(row)"
          >
            <Icon icon="i-lucide-copy" size="size-3" />
          </button>
          <button
            type="button"
            :class="ACTION_CLASS"
            :title="t('ATTRIBUTION.TABLE.EDIT')"
            @click="emit('edit', row)"
          >
            <Icon icon="i-lucide-pencil" size="size-3" />
          </button>
        </div>
      </template>
    </Table>
  </section>
</template>
